import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebaseConfig';

// /oauth/authorize: "Sign in with Resi" for AI apps (Claude, ChatGPT, ...) connecting to the Resi MCP server.
// The user signs in with their normal Resi login, sees which app is asking, and approves or denies.
const wrap = { minHeight: '100vh', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' };
const card = { background: '#FFFFFF', borderRadius: 12, borderTop: '4px solid #D4AF37', padding: 24, width: '100%', maxWidth: 420, boxSizing: 'border-box', color: '#1a1a1a' };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 15, marginBottom: 12 };
const btn = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '10px 18px', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 15, marginRight: 8 };
const ghost = { ...btn, background: '#FFFFFF', color: '#1a1a1a', border: '1px solid #d8c9a8' };
const muted = { fontSize: 14, color: '#5B6270', lineHeight: 1.5 };

export default function OAuthAuthorize() {
  const location = useLocation();
  const { user, userRole, login, loading } = useAuth();
  const params = useMemo(() => Object.fromEntries(new URLSearchParams(location.search)), [location.search]);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`/api/oauth/approve${location.search}`)
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error_description || 'This sign-in link is not valid.'); setInfo(d); })
      .catch((e) => setErr(e.message));
  }, [location.search]);

  const signIn = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { await login(email.trim(), password); } catch (e2) { setErr('Sign-in failed. Check your email and password.'); } finally { setBusy(false); }
  };

  const decide = async (allow) => {
    setBusy(true); setErr('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const r = await fetch('/api/oauth/approve', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify({ params, allow }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.redirect) throw new Error(d.error_description || 'Could not finish. Try again.');
      window.location.assign(d.redirect);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const write = info && (info.scopes || []).includes('resi:write');
  return (
    <div style={wrap}>
      <div style={card}>
        <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>Sign in with Resi</h1>
        {!info && !err && <p style={muted}>Loading...</p>}
        {err && <p style={{ ...muted, color: '#B42318' }} role="alert">{err}</p>}
        {info && (
          <>
            <p style={muted}><strong>{info.clientName}</strong> ({info.redirectHost}) wants to connect to your Resi account.</p>
            <p style={muted}>It will be able to {write ? 'read and change' : 'read'} {userRole === 'Admin' ? 'all Resi data (you are the admin)' : 'only your own deals, pipeline, leads, tasks and account'}. You can disconnect it any time in Profile &gt; Connect Your AI.</p>
            {!loading && !user && (
              <form onSubmit={signIn}>
                <input style={input} type="email" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <input style={input} type="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button style={btn} type="submit" disabled={busy}>Sign in</button>
              </form>
            )}
            {user && (
              <>
                <p style={muted}>Signed in as {user.email}.</p>
                <button style={btn} type="button" disabled={busy} onClick={() => decide(true)}>Approve</button>
                <button style={ghost} type="button" disabled={busy} onClick={() => decide(false)}>Deny</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
